import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Material UI components
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider
} from '@mui/material';

// Material UI icons
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Warehouse as WarehouseIcon
} from '@mui/icons-material';

// Services
import { getWarehouseById, createWarehouse, updateWarehouse } from '../../services/warehouseService';

// Components
import TechnicianManagement from '../../components/TechnicianManagement';

const WarehouseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // State
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    technician_count: 0
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Fetch warehouse data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode) {
        try {
          const warehouseData = await getWarehouseById(id);
          setFormData({
            name: warehouseData.name,
            location: warehouseData.location,
            technician_count: warehouseData.technician_count || 0
          });
          setFetchLoading(false);
        } catch (err) {
          console.error('Error fetching warehouse:', err);
          setError('Failed to load warehouse data. Please try again.');
          setFetchLoading(false);
        }
      }
    };

    fetchData();
  }, [id, isEditMode]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name) {
      errors.name = 'Warehouse name is required';
    }

    if (!formData.location) {
      errors.location = 'Warehouse location is required';
    }

    if (formData.technician_count < 0) {
      errors.technician_count = 'Number of technicians cannot be negative';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await updateWarehouse(id, formData);
      } else {
        await createWarehouse(formData);
      }

      navigate('/locations');
    } catch (err) {
      console.error('Error saving warehouse:', err);
      setError(err.message || 'Failed to save warehouse. Please try again.');
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Dashboard
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/locations');
          }}
        >
          Locations
        </Link>
        <Typography color="text.primary">
          {isEditMode ? 'Edit Warehouse' : 'Add Warehouse'}
        </Typography>
      </Breadcrumbs>

      {/* Page title */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <WarehouseIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Warehouse' : 'Add New Warehouse'}
        </Typography>
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Warehouse Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Warehouse Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={Boolean(formErrors.name)}
                helperText={formErrors.name || "Enter a unique name for the warehouse (e.g., Main Warehouse, North DC)"}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Warehouse Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                error={Boolean(formErrors.location)}
                helperText={formErrors.location || "Enter the complete physical address of the warehouse"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Technicians"
                name="technician_count"
                value={formData.technician_count}
                onChange={handleChange}
                error={Boolean(formErrors.technician_count)}
                helperText={formErrors.technician_count || "Enter the number of technicians at this warehouse"}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/locations')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Warehouse')}
          </Button>
        </Box>
      </form>

      {/* Technician Management - Only show in edit mode */}
      {isEditMode && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <TechnicianManagement
            warehouseId={parseInt(id)}
            warehouseName={formData.name}
          />
        </Box>
      )}
    </Container>
  );
};

export default WarehouseForm;
